import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import {
  CalendarConnectionStatus,
  CalendarSyncState,
  type CalendarProvider,
  type CalendarConnectionDto,
  type UpdateCalendarPreferenceRequest,
  type UpdateCalendarConnectionRequest,
  type CampaignCalendarVisibility,
} from '@signalvote/shared';
import { queryKeys } from '@/lib/queryKeys';
import { useCampaignStore } from '@/stores';

const api = getApiClient();

// ============================================================================
// Connection Management
// ============================================================================

/**
 * Fetch all calendar connections for the current user.
 * Polls every 2s while any connection is syncing, then invalidates events when done.
 */
export function useCalendarConnections() {
  const queryClient = useQueryClient();
  const wasSyncingRef = useRef(false);

  const query = useQuery({
    queryKey: queryKeys.calendar.connections(),
    queryFn: () => api.calendar.getConnections(),
    staleTime: 60 * 1000, // 1 minute
  });

  const connections: CalendarConnectionDto[] | undefined = query.data;
  const isSyncing = connections?.some(c => c.syncState === CalendarSyncState.Syncing) ?? false;

  // Poll while syncing
  useEffect(() => {
    if (!isSyncing) {
      // Sync just finished — invalidate calendar events so UI refreshes
      if (wasSyncingRef.current) {
        wasSyncingRef.current = false;
        queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });
      }
      return;
    }

    wasSyncingRef.current = true;
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar.connections() });
    }, 2000);

    return () => clearInterval(interval);
  }, [isSyncing, queryClient]);

  return { ...query, isSyncing };
}

/**
 * Returns whether any calendar connection is non-Active (expired, revoked, or errored).
 * Shares the same TanStack Query cache as useCalendarConnections().
 */
export function useHasUnhealthyCalendarConnections() {
  const { data: connections } = useCalendarConnections();
  return connections?.some(
    (c) => c.status !== CalendarConnectionStatus.Active,
  ) ?? false;
}

/**
 * Start OAuth flow for connecting a calendar provider.
 * Returns an authorization URL to redirect the user to.
 */
export function useStartCalendarConnection() {
  return useMutation({
    mutationFn: ({
      provider,
      redirectUri,
    }: {
      provider: CalendarProvider;
      redirectUri: string;
    }) => api.calendar.startConnection(provider.toLowerCase(), { redirectUri }),
  });
}

/**
 * Disconnect a calendar connection
 */
export function useDisconnectCalendar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: string) => api.calendar.disconnectAccount(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });
    },
  });
}

/**
 * Update a calendar connection's display name and/or color
 */
export function useUpdateCalendarConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      connectionId,
      request,
    }: {
      connectionId: string;
      request: Partial<UpdateCalendarConnectionRequest>;
    }) => api.calendar.updateConnection(connectionId, { displayName: null, color: null, ...request }),
    onSuccess: () => {
      // Invalidate all calendar queries (connections, events, etc.)
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });
    },
  });
}

// ============================================================================
// Calendar Preferences
// ============================================================================

/**
 * Fetch calendars and their sync preferences for a specific connection
 */
export function useCalendarsForConnection(connectionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.calendar.calendars(connectionId!),
    queryFn: () => api.calendar.getCalendars(connectionId!),
    enabled: !!connectionId,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Refresh the list of calendars from the provider.
 * Useful when user creates a new calendar in Google/Microsoft.
 */
export function useRefreshCalendarList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: string) => api.calendar.refreshCalendars(connectionId),
    onSuccess: (_, connectionId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.calendar.calendars(connectionId),
      });
    },
  });
}

/**
 * Update sync preferences for a specific calendar
 */
export function useUpdateCalendarPreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      connectionId,
      calendarId,
      request,
    }: {
      connectionId: string;
      calendarId: string;
      request: Partial<UpdateCalendarPreferenceRequest>;
    }) =>
      api.calendar.updateCalendarPreference(connectionId, calendarId, {
        readEventsEnabled: null,
        writeShiftsEnabled: null,
        isPrimaryWriteCalendar: null,
        color: null,
        ...request,
      }),
    onSuccess: (_, { connectionId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.calendar.calendars(connectionId),
      });
      // Also invalidate events so color changes are reflected in the calendar view
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });
    },
  });
}

// ============================================================================
// Events
// ============================================================================

/**
 * Fetch calendar events within a date range.
 * Returns both personal events and campaign shifts in a unified format.
 */
export function useCalendarEvents(start: Date | undefined, end: Date | undefined) {
  return useQuery({
    queryKey: queryKeys.calendar.events(start?.toISOString() ?? '', end?.toISOString() ?? ''),
    queryFn: () => api.calendar.getEvents({ start: start!.toISOString(), end: end!.toISOString() }),
    enabled: !!start && !!end,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useKeyDates() {
  return useQuery({
    queryKey: queryKeys.calendar.keyDates(),
    queryFn: () => api.keyDates.getKeyDates(),
  });
}

/**
 * Manually trigger a calendar sync.
 * Syncs events from connected calendars and pushes shifts if configured.
 */
export function useTriggerCalendarSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.calendar.triggerSync(),
    onSuccess: () => {
      // The backend already set syncState=Syncing before returning.
      // Optimistically update the cache so polling starts immediately
      // without an extra network round trip.
      queryClient.setQueryData<CalendarConnectionDto[]>(
        queryKeys.calendar.connections(),
        (old) => old?.map(c => ({ ...c, syncState: CalendarSyncState.Syncing })),
      );
    },
  });
}

// ============================================================================
// Conflicts
// ============================================================================

// ============================================================================
// Cross-Campaign Visibility
// ============================================================================

/**
 * Fetch cross-campaign calendar visibility preferences.
 * Returns other campaigns the user belongs to with their visibility setting.
 */
export function useCampaignVisibility() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.calendar.campaignVisibility(),
    queryFn: () => api.calendar.getCampaignVisibility(),
    enabled: !!selectedCampaign,
    staleTime: 60 * 1000,
  });
}

/**
 * Update visibility setting for a specific campaign's events.
 * Invalidates both visibility prefs and calendar events on success.
 */
export function useUpdateCampaignVisibility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      visibility,
    }: {
      campaignId: string;
      visibility: CampaignCalendarVisibility;
    }) => api.calendar.updateCampaignVisibility(campaignId, { visibility }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar.campaignVisibility() });
      // Also invalidate calendar events so they re-fetch with new visibility
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });
    },
  });
}
